// src/encounter/services/use-lab-requisitions.ts
// Scrips.Patient's lab requisitions for one encounter (PROD-2841, Patient #1022):
//   GET api/Patients/{patientId}/Encounters/{encounterId}/LabRequisitions
//   -> { patientId, encounterId, storeAvailable, testsAvailable, requisitions: [{ fulfilmentMode, status,
//        labVendorName, tests: [{ display, status, ... }] }] }   (never a cost)
// Requisitions are made at sign. Wrap-up's order view reads them for the Patient's choice tests
// (PROD-2849). Any failure (the store's script not run yet, a 404) is "none".
import { useContext } from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { LabRequisitionLite } from '../lib/lab-print-extras';

interface LabRequisitionsResponse {
  requisitions?: LabRequisitionLite[] | null;
}

/** Used only when no QueryClientProvider is mounted (isolated page tests): it never fetches. */
const idleClient = new QueryClient({ defaultOptions: { queries: { enabled: false, retry: false } } });

export function useLabRequisitions(patientId: string | null | undefined, encounterId: string | null | undefined, enabled = true) {
  const p = patientId?.trim() ?? '';
  const e = encounterId?.trim() ?? '';
  const client = useContext(QueryClientContext);
  return useQuery<LabRequisitionLite[]>({
    queryKey: ['lab-requisitions', p, e],
    enabled: enabled && !!client && !!p && !!e,
    retry: false,
    staleTime: 30_000,
    queryFn: async ({ signal }) => {
      try {
        const res = await get<LabRequisitionsResponse>(
          `/api/Patients/${encodeURIComponent(p)}/Encounters/${encodeURIComponent(e)}/LabRequisitions`,
          { signal },
        );
        return res?.requisitions ?? [];
      } catch {
        // DELIBERATE: no requisitions to show (unsigned encounter, store not created yet, or a failed
        // read). The order view then lists only this session's Patient's choice tests.
        return [];
      }
    },
  }, client ?? idleClient);
}
